import { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus,
  Link2,
  Eye,
  ShoppingCart,
  MoreVertical,
  Copy,
  Pencil,
  Trash2,
  Upload,
  Check,
  ExternalLink,
  Sparkles,
  FileImage,
  DollarSign,
  X,
  Loader2,
  Film,
  Image as ImageIcon,
  FileText,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

const currencies = ["EUR"];

export default function PaymentLinksPage() {
  const { t } = useLanguage();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    currency: "EUR",
    blur_preview: true,
    button_text: "Buy Now",
    success_message: "Thank you for your purchase!",
  });

  const [mediaFile, setMediaFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [existingMedia, setExistingMedia] = useState(null);
  const [removingMedia, setRemovingMedia] = useState(false);
  const [videoThumb, setVideoThumb] = useState(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const { data } = await axios.get(`${API}/payment-links`, {
        withCredentials: true,
      });
      setLinks(data);
    } catch (error) {
      console.error("Failed to fetch links:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      currency: "EUR",
      blur_preview: true,
      button_text: "Buy Now",
      success_message: "Thank you for your purchase!",
    });
    setMediaFile(null);
    setPreviewFile(null);
    setExistingMedia(null);
    setVideoThumb(null);
    setEditingLink(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      description: link.description || "",
      price: link.price.toString(),
      currency: link.currency,
      blur_preview: link.blur_preview,
      button_text: link.button_text,
      success_message: link.success_message,
    });
    setMediaFile(null);
    setPreviewFile(null);
    // Load existing media info from enriched link data
    if (link.media_info) {
      setExistingMedia(link.media_info);
    } else {
      setExistingMedia(null);
    }
    setDialogOpen(true);
  };

  // Compress image before upload
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      if (!file.type?.startsWith("image/")) {
        resolve(file);
        return;
      }
      const img = document.createElement("img");
      const canvas = document.createElement("canvas");
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(new File([blob], file.name, { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
      };

      let link;
      if (editingLink) {
        const { data } = await axios.put(
          `${API}/payment-links/${editingLink.id}`,
          payload
        );
        link = data;
        toast.success(t("links.linkUpdated"));
      } else {
        const { data } = await axios.post(`${API}/payment-links`, payload);
        link = data;
        toast.success(t("links.linkCreated"));
      }

      // Close dialog and refresh immediately
      setDialogOpen(false);
      resetForm();
      setSaving(false);
      fetchLinks();

      // Upload media in background
      if (mediaFile) {
        const compressed = await compressImage(mediaFile);
        const mediaFormData = new FormData();
        mediaFormData.append("file", compressed);
        axios.put(
          `${API}/payment-links/${link.id}/media`,
          mediaFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        ).then(() => {
          toast.success(t("links.mediaUploaded"));
          fetchLinks();
        }).catch(() => {
          toast.error("Media upload failed, try editing the link to re-upload");
        });
      }
    } catch (error) {
      console.error("Failed to save link:", error);
      toast.error(error.response?.data?.detail || "Failed to save payment link");
      setSaving(false);
    }
  };

  const handleDelete = async (linkId) => {
    setDeleting(linkId);
    try {
      await axios.delete(`${API}/payment-links/${linkId}`, {
        withCredentials: true,
      });
      toast.success(t("links.linkDeleted"));
      fetchLinks();
    } catch (error) {
      console.error("Failed to delete link:", error);
      toast.error(t("links.failedToDelete"));
    } finally {
      setDeleting(null);
    }
  };

  const handleRemoveMedia = async () => {
    if (!editingLink) return;
    setRemovingMedia(true);
    try {
      await axios.delete(`${API}/payment-links/${editingLink.id}/media`, {
        withCredentials: true,
      });
      setExistingMedia(null);
      toast.success(t("links.mediaRemoved"));
    } catch (error) {
      console.error("Failed to remove media:", error);
      toast.error("Failed to remove media");
    } finally {
      setRemovingMedia(false);
    }
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      setExistingMedia(null);
      setVideoThumb(null);
      // Generate thumbnail for video files
      if (file.type?.startsWith("video/")) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        const url = URL.createObjectURL(file);
        video.src = url;
        video.onloadeddata = () => {
          video.currentTime = 0.5;
        };
        video.onseeked = () => {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext("2d").drawImage(video, 0, 0);
          setVideoThumb(canvas.toDataURL("image/jpeg", 0.7));
          URL.revokeObjectURL(url);
        };
      }
    }
  };

  const getMediaTypeLabel = (contentType) => {
    if (!contentType) return "File";
    if (contentType.startsWith("image/")) return "Photo";
    if (contentType.startsWith("video/")) return "Video";
    if (contentType.includes("pdf")) return "PDF";
    return "File";
  };

  const getMediaTypeIcon = (contentType) => {
    if (!contentType) return FileText;
    if (contentType.startsWith("image/")) return ImageIcon;
    if (contentType.startsWith("video/")) return Film;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const copyLink = async (slug) => {
    const url = `${BASE_URL}/p/${slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(slug);
    toast.success(t("links.linkCopied"));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-11 w-36" />
        </div>
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="payment-links-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("links.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("links.subtitle")}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog} 
              className="h-11 px-6 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
              data-testid="create-link-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t("links.create")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {editingLink ? t("links.edit") : t("links.create")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-2 pb-12 sm:pb-2">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">{t("links.linkTitle")}</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder={t("links.titlePlaceholder")}
                  className="h-11 rounded-xl"
                  required
                  data-testid="link-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">{t("links.description")}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder={t("links.descriptionPlaceholder")}
                  rows={2}
                  className="rounded-xl resize-none"
                  data-testid="link-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price" className="text-sm font-medium">{t("links.price")}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">€</span>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0.50"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder="9.99"
                      className="h-11 pl-10 rounded-xl"
                      required
                      data-testid="link-price-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("links.currency")}</Label>
                  <div className="h-11 rounded-xl border border-input bg-muted/50 flex items-center px-4 text-sm font-medium">
                    EUR
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">{t("links.uploadMedia")}</Label>
                
                {/* Existing media preview (unblurred for creator) */}
                {existingMedia && !mediaFile ? (
                  <div className="rounded-xl border border-border overflow-hidden" data-testid="existing-media-preview">
                    <div className="flex items-center gap-4 p-3">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0 relative">
                        <img
                          src={`${API}/media-thumbnail/${existingMedia.file_id}`}
                          alt={existingMedia.filename}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {existingMedia.content_type?.startsWith("video/") && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-8 h-8 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center">
                              <Film className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Info + Actions */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{existingMedia.filename}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {getMediaTypeLabel(existingMedia.content_type)}
                          {existingMedia.size ? ` — ${formatFileSize(existingMedia.size)}` : ""}
                        </p>
                        <div className="flex items-center gap-2 mt-2.5">
                          <label htmlFor="media-replace" className="cursor-pointer">
                            <div className="h-7 px-2.5 rounded-lg border border-border hover:bg-muted flex items-center gap-1.5 text-xs font-medium transition-colors">
                              <RefreshCw className="h-3 w-3" />
                              {t("links.replaceMedia")}
                            </div>
                          </label>
                          <input type="file" id="media-replace" className="hidden" onChange={handleMediaChange} accept="*/*" />
                          <button
                            type="button"
                            onClick={handleRemoveMedia}
                            disabled={removingMedia}
                            className="h-7 px-2.5 rounded-lg border border-destructive/30 hover:bg-destructive/10 flex items-center gap-1.5 text-xs font-medium text-destructive transition-colors disabled:opacity-50"
                            data-testid="remove-media-btn"
                          >
                            {removingMedia ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            {t("links.removeMedia")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : mediaFile ? (
                  /* New file selected — local preview */
                  <div className="rounded-xl border border-primary/50 overflow-hidden bg-primary/5" data-testid="new-media-preview">
                    <div className="flex items-center gap-4 p-3">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0 relative">
                        {mediaFile.type?.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(mediaFile)}
                            alt={mediaFile.name}
                            className="w-full h-full object-cover"
                          />
                        ) : mediaFile.type?.startsWith("video/") ? (
                          <>
                            {videoThumb ? (
                              <img src={videoThumb} alt="Video thumbnail" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-zinc-700 to-zinc-900" />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <div className="w-8 h-8 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center">
                                <Film className="h-4 w-4 text-white" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      {/* Info + Actions */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{mediaFile.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {getMediaTypeLabel(mediaFile.type)}
                          {mediaFile.size ? ` — ${formatFileSize(mediaFile.size)}` : ""}
                        </p>
                        <div className="flex items-center gap-2 mt-2.5">
                          <label htmlFor="media-change" className="cursor-pointer">
                            <div className="h-7 px-2.5 rounded-lg border border-border hover:bg-muted flex items-center gap-1.5 text-xs font-medium transition-colors">
                              <RefreshCw className="h-3 w-3" />
                              {t("links.replaceMedia")}
                            </div>
                          </label>
                          <input type="file" id="media-change" className="hidden" onChange={handleMediaChange} accept="*/*" />
                          <button
                            type="button"
                            onClick={() => { setMediaFile(null); setVideoThumb(null); }}
                            className="h-7 px-2.5 rounded-lg border border-destructive/30 hover:bg-destructive/10 flex items-center gap-1.5 text-xs font-medium text-destructive transition-colors"
                          >
                            <X className="h-3 w-3" />
                            {t("links.removeMedia")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* No media — upload zone */
                  <div className="border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 border-border hover:border-primary/50 hover:bg-muted/50">
                    <input
                      type="file"
                      id="media"
                      className="hidden"
                      onChange={handleMediaChange}
                      accept="*/*"
                    />
                    <label htmlFor="media" className="cursor-pointer flex flex-col items-center">
                      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-2">
                        <Upload className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {t("links.uploadMediaDesc")}
                      </span>
                      <span className="text-xs text-muted-foreground mt-0.5">
                        {t("links.uploadMediaTypes")}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                <div>
                  <Label htmlFor="blur" className="text-sm font-medium">{t("links.blurPreview")}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("links.blurPreviewDesc")}</p>
                </div>
                <Switch
                  id="blur"
                  checked={formData.blur_preview}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, blur_preview: checked })
                  }
                  data-testid="link-blur-switch"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="buttonText" className="text-sm font-medium">{t("links.buttonText")}</Label>
                  <Input
                    id="buttonText"
                    value={formData.button_text}
                    onChange={(e) =>
                      setFormData({ ...formData, button_text: e.target.value })
                    }
                    placeholder={t("links.buttonTextPlaceholder")}
                    className="h-10 rounded-xl"
                    data-testid="link-button-text-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="successMessage" className="text-sm font-medium">{t("links.successMessage")}</Label>
                  <Input
                    id="successMessage"
                    value={formData.success_message}
                    onChange={(e) =>
                      setFormData({ ...formData, success_message: e.target.value })
                    }
                    placeholder={t("links.successMessagePlaceholder")}
                    className="h-10 rounded-xl"
                    data-testid="link-success-message-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  onClick={() => setDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-xl"
                  disabled={saving}
                  data-testid="save-link-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("links.saving")}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {t("common.save")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {links.length === 0 ? (
        <Card className="border-border border-dashed">
          <CardContent className="py-20 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#0B1F3A]/10 to-[#3A6D8C]/10 flex items-center justify-center mx-auto mb-6">
              <Link2 className="h-10 w-10 text-[#0B1F3A] dark:text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t("links.noLinks")}</h3>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">{t("links.createFirst")}</p>
            <Button 
              onClick={openCreateDialog}
              className="h-12 px-8 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              {t("links.create")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {links.map((link, index) => (
            <Card
              key={link.id}
              className="group border-border hover:shadow-xl hover:shadow-[#0B1F3A]/5 dark:hover:shadow-white/5 transition-all duration-300 overflow-hidden"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`payment-link-${link.id}`}
            >
              <CardContent className="p-4 sm:p-6">
                {/* Mobile layout: stacked */}
                <div className="flex flex-col gap-3 sm:hidden">
                  {/* Row 1: Title + Status + Menu */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors">
                        {link.title}
                      </h3>
                      {link.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {link.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span
                        className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                          link.is_active
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {link.is_active ? t("links.active") : t("links.inactive")}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => openEditDialog(link)} className="cursor-pointer">
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("links.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(link.id)}
                            className="text-destructive focus:text-destructive cursor-pointer"
                            disabled={deleting === link.id}
                          >
                            {deleting === link.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            {t("links.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Row 2: Price + Stats */}
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold tracking-tight">
                      {formatCurrency(link.price, link.currency)}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Eye className="h-3.5 w-3.5" />
                        <span className="font-medium">{link.views}</span> {t("links.views")}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <ShoppingCart className="h-3.5 w-3.5" />
                        <span className="font-medium">{link.sales}</span> {t("links.sales")}
                      </span>
                    </div>
                  </div>

                  {/* Row 3: Action buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-9 rounded-lg text-xs"
                      onClick={() => copyLink(link.slug)}
                      data-testid={`copy-link-${link.id}`}
                    >
                      {copiedId === link.slug ? (
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                      ) : (
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {copiedId === link.slug ? t("links.copied") : t("links.copyLink")}
                    </Button>
                    <a
                      href={`/p/${link.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full h-9 rounded-lg text-xs"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        {t("links.preview")}
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Desktop layout: horizontal */}
                <div className="hidden sm:flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
                        {link.title}
                      </h3>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          link.is_active
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {link.is_active ? t("links.active") : t("links.inactive")}
                      </span>
                    </div>
                    {link.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {link.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <div className="p-1.5 rounded-lg bg-muted">
                          <Eye className="h-4 w-4" />
                        </div>
                        <span className="font-medium">{link.views}</span> {t("links.views")}
                      </span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <div className="p-1.5 rounded-lg bg-muted">
                          <ShoppingCart className="h-4 w-4" />
                        </div>
                        <span className="font-medium">{link.sales}</span> {t("links.sales")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-3xl font-bold tracking-tight">
                        {formatCurrency(link.price, link.currency)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 rounded-xl hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                        onClick={() => copyLink(link.slug)}
                        data-testid={`copy-link-desktop-${link.id}`}
                      >
                        {copiedId === link.slug ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>

                      <a
                        href={`/p/${link.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-10 w-10 rounded-xl hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => openEditDialog(link)} className="cursor-pointer">
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("links.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(link.id)}
                            className="text-destructive focus:text-destructive cursor-pointer"
                            disabled={deleting === link.id}
                          >
                            {deleting === link.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            {t("links.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
